import { SMSMessageDto } from '@act/shared/data-transfer-objects';
import { Button, FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useToast } from 'react-components/app/ReusableToast';
import { SelectOption } from 'react-components/components/search-bar';
import { TrackingProvider } from 'react-components/tracking';
import { useForm } from 'react-hook-form';
import { callsRequests } from 'redux/requests/calls.request';
import { membersRequests } from 'redux/requests/members.request';
import { updateSubjectId } from 'redux/slices/callsSlice';
import { createPhoneNumber } from 'redux/slices/memberSlice';
import { assignSMSMessage } from 'redux/slices/smsMessagesSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';

import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import StyledAlert from 'toolkit/alerts/styled-alert';
import { z } from 'zod';
import Loader from '../workflows/loader';

// Zod Schema
const AssignPhoneNumberSchema = z.object({
  interactableId: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .nullable()
    .refine(val => val !== null, {
      message: 'Please select a member',
    }),
  shouldAssignPhoneNumber: z.boolean(),
  type: z.string().min(1, 'Phone type is required'),
  carrier: z.string(),
  description: z
    .string()
    .max(20, 'Description must be less than 20 characters')
    .optional(),
  number: z.string().min(1, 'Phone number is required'),
});

export type AssignPhoneNumberForm = z.infer<typeof AssignPhoneNumberSchema>;

export interface AssignPhoneNumberFormProps {
  phoneNumber: string;
  callId?: number;
  sms?: SMSMessageDto;
  onSuccess?: () => void;
  onCancel?: () => void;
  showActions?: boolean;
  showAlert?: boolean;
  showTitle?: boolean;
}

export const AssignPhoneNumberFormComponent = ({
  phoneNumber,
  callId,
  sms,
  onSuccess,
  onCancel,
  showActions = true,
  showAlert = true,
  showTitle = true,
}: AssignPhoneNumberFormProps) => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const {
    control,
    getValues,
    reset,
    formState,
    handleSubmit,
    setValue,
    watch,
  } = useForm<AssignPhoneNumberForm>({
    mode: 'onChange',
    resolver: zodResolver(AssignPhoneNumberSchema),
    defaultValues: {
      interactableId: null,
      shouldAssignPhoneNumber: false,
      type: '',
      carrier: '',
      description: '',
      number: '',
    },
  });

  const shouldAssignPhoneNumber = watch('shouldAssignPhoneNumber');
  const selectedMemberId = watch('interactableId');

  const { isValid } = formState;

  const [phoneDetailsResponse, phoneDetailsLoading, getPhoneDetails] =
    useApiCall(callsRequests.getPhoneDetailsFromTwilio);

  const [typeaheadUsers, isLoadingTypeahead, getTypeaheadUsers] = useApiCall(
    membersRequests.getPaginatedMembersForTable
  );

  // Check if the selected member already has this phone number
  // We can check this immediately from the label which already indicates this
  const selectedMemberHasPhoneNumber = useMemo(() => {
    if (!selectedMemberId?.label) {
      return false;
    }
    // Check if the label contains the indicator text
    return selectedMemberId.label.includes('(assigned to this member)');
  }, [selectedMemberId]);

  const [isAssigning, setIsAssigning] = useState(false);

  const [typeaheadOptions, setTypeaheadOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (typeaheadUsers?.users) {
      setTypeaheadOptions(
        typeaheadUsers.users.map(u => {
          const baseName = `${u.firstName} ${u.lastName}`;

          // Check if this member has the phone number assigned
          const hasPhoneNumber =
            phoneNumber &&
            u.phoneNumbers?.some(phone => phone.number === phoneNumber);

          const label = hasPhoneNumber
            ? `${baseName} (assigned to this member)`
            : baseName;

          return {
            label,
            value: u.id,
          };
        })
      );
    } else {
      setTypeaheadOptions([]);
    }
  }, [typeaheadUsers, phoneNumber]);

  async function setTypeaheadSearchTerm(searchTerm: string) {
    const overrideFilters = {
      searchTerm,
      'page-start': 0,
      'page-size': 10,
      includeCount: true,
      orderBy: null,
      orderDirection: null,
      includeDetails: true,
    };
    const filterConfig = {
      ...overrideFilters,
    };
    void getTypeaheadUsers({ params: filterConfig });
  }

  useEffect(() => {
    if (phoneNumber) {
      // Extract only digits from the phone number
      const digitsOnly = phoneNumber.replace(/\D/g, '');
      // Remove leading 1 if present (US country code)
      const withoutCountryCode =
        digitsOnly.startsWith('1') && digitsOnly.length === 11
          ? digitsOnly.slice(1)
          : digitsOnly;
      // Take only the last 10 digits to ensure we have a valid US phone number
      const formattedNumber = withoutCountryCode.slice(-10);
      reset({
        ...getValues(),
        number: formattedNumber,
      });
      getPhoneDetails({ params: { phoneNumber } });
    }
  }, [phoneNumber]);

  useEffect(() => {
    reset({
      ...getValues(),
      type: phoneDetailsResponse?.type
        ? `${phoneDetailsResponse.type
            .slice(0, 1)
            .toLocaleUpperCase()}${phoneDetailsResponse.type.slice(1)}`
        : 'Unknown',
      carrier: phoneDetailsResponse?.carrier || 'not found',
    });
  }, [phoneDetailsResponse]);

  // Auto-uncheck "assign phone number" if selected member already has it
  useEffect(() => {
    if (selectedMemberHasPhoneNumber && shouldAssignPhoneNumber) {
      setValue('shouldAssignPhoneNumber', false);
    }
  }, [selectedMemberHasPhoneNumber, shouldAssignPhoneNumber, setValue]);

  const isSubmitting = isAssigning || formState.isSubmitting;

  const formConfig = useMemo(
    () =>
      [
        {
          type: 'input',
          name: 'interactableId',
          label: 'Search by member name',
          input: 'searchBar',
          trackingName: 'member-search-input',
          props: {
            options: typeaheadOptions,
            isLoading: isLoadingTypeahead,
            onTypeaheadSearch: setTypeaheadSearchTerm,
            fullWidth: true,
            trackingName: 'member-search-input',
            onSearch: (searchTerm: string | SelectOption | null) => {
              if (searchTerm && typeof searchTerm === 'object') {
                // Use the actual SelectOption object
                setValue('interactableId', searchTerm, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              } else {
                // If it's a string or null, set to null
                setValue('interactableId', null, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
            },
          },
        },
        // Only show checkbox if member doesn't already have the phone number
        ...(!selectedMemberHasPhoneNumber
          ? [
              {
                type: 'input',
                name: 'shouldAssignPhoneNumber',
                label: 'Also assign this phone number to the member',
                input: 'checkbox',
                trackingName: 'assign-phone-number-checkbox',
              },
            ]
          : []),
        ...(shouldAssignPhoneNumber
          ? [
              {
                type: 'group',
                items: [
                  {
                    type: 'input',
                    name: 'type',
                    label: 'Phone type',
                    input: 'text',
                    trackingName: 'phone-type-input',
                    props: {
                      disabled: true,
                      fullWidth: true,
                    },
                  },
                  {
                    type: 'input',
                    name: 'description',
                    label: 'Optional label',
                    input: 'text',
                    trackingName: 'phone-description-input',
                    props: {
                      fullWidth: true,
                      characterLimit: 20,
                    },
                  },
                ],
              },
              {
                type: 'input',
                name: 'number',
                label: 'Phone Number',
                input: 'phone',
                trackingName: 'phone-number-input',
                disabled: true,
              },
            ]
          : []),
      ] as FormConfig<AssignPhoneNumberForm>,
    [
      typeaheadOptions,
      isLoadingTypeahead,
      setTypeaheadSearchTerm,
      setValue,
      shouldAssignPhoneNumber,
      selectedMemberHasPhoneNumber,
    ]
  );

  async function onSubmit(data: AssignPhoneNumberForm) {
    try {
      const memberId = data.interactableId?.value;
      if (!memberId) {
        toast('Please select a member', 'error');
        return;
      }

      setIsAssigning(true);

      // Only create phone number if checkbox is checked
      if (data.shouldAssignPhoneNumber) {
        await dispatch(
          createPhoneNumber({
            params: { userId: memberId },
            body: {
              carrier: data.carrier,
              description: data.description || '',
              number: '+1'.concat(data.number.replace(/\D/g, '').slice(-10)),
              primary: false,
              type: data.type.toLocaleLowerCase(),
            },
          })
        );
      }

      // Always assign the call or SMS to the member
      if (sms) {
        await dispatch(
          assignSMSMessage({
            body: { subjectId: memberId },
            params: { id: sms?.id },
          })
        );
      } else if (callId) {
        await dispatch(
          updateSubjectId({
            params: { id: callId },
            body: {
              subjectId: memberId,
            },
          })
        );
      }

      const successMessage = data.shouldAssignPhoneNumber
        ? 'Phone call and number assigned successfully!'
        : 'Phone call assigned successfully!';
      toast(successMessage, 'success');
      reset();
      onSuccess?.();
    } catch (error) {
      toast('An error occurred. Please try again.', 'error');
    } finally {
      setIsAssigning(false);
    }
  }

  return (
    <TrackingProvider contextName="assign-phone-number-form">
      {phoneDetailsLoading && <Loader />}
      {!phoneDetailsLoading && (
        <Fragment>
          {showAlert && shouldAssignPhoneNumber && (
            <>
              <StyledAlert
                message="This number will be added to the member's profile. This call and all future communication from this number will appear in the member's feed."
                severity="warning"
                variant="tonal"
              />
              <br />
            </>
          )}
          {showTitle && (
            <Typography variant="titleMedium">
              {shouldAssignPhoneNumber
                ? 'Select a member for this call and number'
                : 'Select a member for this call'}
            </Typography>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ margin: '8px 0 16px' }}>
              <FormBuilder
                config={formConfig}
                control={control}
                formState={formState}
                disabled={isSubmitting}
              />
            </Box>
            {showActions && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'right',
                  gap: '8px',
                  marginTop: '16px',
                }}
              >
                {onCancel && (
                  <Button
                    onClick={onCancel}
                    disabled={isSubmitting}
                    trackingName="cancel-button"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  disabled={!isValid || isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                  type="submit"
                  variant="contained"
                  startIcon={
                    isSubmitting ? <CircularProgress size={20} /> : null
                  }
                  trackingName="assign-number-button"
                >
                  {isSubmitting
                    ? 'Assigning...'
                    : shouldAssignPhoneNumber
                    ? 'Assign call and number'
                    : 'Assign call'}
                </Button>
              </Box>
            )}
          </form>
        </Fragment>
      )}
    </TrackingProvider>
  );
};
